/** Visible Brook identity: rock/flower role, legal-next hint, accepted water,
 * local orthogonal edges and current flower/pool arrival. Scores, elapsed time,
 * seeds and remote path edits do not replace unchanged water or restart pulses. */
export function brookArtworkKey(state, cell, index) {
  const columns = state.data.columns || 5,
    end = columns ** 2 - 1;
  const route = state.data.path,
    position = route.indexOf(index);
  const edges =
    position < 0
      ? []
      : [route[position - 1], route[position + 1]].filter(
          (neighbor) =>
            neighbor !== undefined &&
            Math.abs((neighbor % columns) - (index % columns)) +
              Math.abs(Math.floor(neighbor / columns) - Math.floor(index / columns)) ===
              1,
        );
  return JSON.stringify([
    columns,
    cell.bridge,
    Boolean(cell.blocked),
    cell.checkpoint || 0,
    Boolean(cell.destination),
    index === 0 ? 'spring' : index === end ? 'pool' : 'bank',
    position >= 0,
    edges,
    position === route.length - 1 && (cell.checkpoint > 0 || index === end),
  ]);
}

export function brookCellArtwork(ctx, state, cell, index, kit) {
  const { svg, add, path, circle } = kit;
  const art = svg(ctx, 'game-piece-art game-brook-art');
  const columns = state.data.columns || 5,
    end = columns ** 2 - 1;
  const route = state.data.path,
    position = route.indexOf(index);
  const arrived = position === route.length - 1 && (cell.checkpoint > 0 || index === end);
  art.setAttribute('data-brook-arrival', arrived ? String(cell.checkpoint || 'pool') : '');
  art.setAttribute('data-brook-watered', String(position >= 0));
  const id = `brook-${globalThis.crypto?.randomUUID?.() || index}`;
  const defs = add(ctx, art, 'defs', {});
  const paint = (name, stops, radial = false) => {
    const gradient = add(ctx, defs, radial ? 'radialGradient' : 'linearGradient', {
      id: `${id}-${name}`,
      ...(radial
        ? { cx: '30%', cy: '20%', r: '90%' }
        : { x1: '0%', y1: '0%', x2: '100%', y2: '100%' }),
    });
    for (const [offset, colour] of stops)
      add(ctx, gradient, 'stop', { offset, 'stop-color': colour });
    return `url(#${id}-${name})`;
  };
  const ground = paint('bank', [
    ['0%', 'color-mix(in srgb,var(--game-colour-0) 38%,var(--game-night))'],
    ['100%', 'var(--game-night)'],
  ]);
  add(ctx, art, 'rect', { width: 100, height: 100, fill: ground, opacity: 0.44 });
  for (const [x, y, size] of [
    [13, 19, 3],
    [84, 75, 4],
    [76, 15, 2],
  ]) {
    circle(ctx, art, x, y, size, { fill: 'var(--game-colour-0)', opacity: 0.3 });
    path(ctx, art, `M${x} ${y}q-3 -6 1 -9`, {
      fill: 'none',
      stroke: 'var(--game-colour-0)',
      opacity: 0.35,
      'stroke-width': 1,
    });
  }
  if (cell.blocked) {
    const stone = paint(
      'stone',
      [
        ['0%', 'var(--game-paper)'],
        ['35%', 'var(--game-neutral)'],
        ['100%', 'var(--game-paper-ink)'],
      ],
      true,
    );
    add(ctx, art, 'ellipse', {
      cx: 50,
      cy: 76,
      rx: 37,
      ry: 13,
      fill: 'var(--game-night)',
      opacity: 0.55,
    });
    path(ctx, art, 'M18 63Q9 48 26 31Q39 17 58 22Q80 21 87 46Q93 67 75 76Q44 87 18 63Z', {
      class: 'brook-rock',
      fill: stone,
      stroke: 'var(--game-line)',
      'stroke-width': 1.5,
    });
    path(ctx, art, 'M28 34Q41 25 58 29M23 48Q20 54 27 61', {
      fill: 'none',
      stroke: 'var(--game-white)',
      'stroke-width': 3,
      'stroke-linecap': 'round',
      opacity: 0.4,
    });
    path(ctx, art, 'M60 69Q74 73 80 62', {
      fill: 'none',
      stroke: 'var(--game-colour-0)',
      'stroke-width': 6,
      'stroke-linecap': 'round',
      opacity: 0.65,
    });
    return art;
  }
  const water = paint('water', [
    ['0%', 'color-mix(in srgb,var(--game-colour-0) 55%,var(--game-white))'],
    ['38%', 'var(--game-colour-4)'],
    ['64%', 'color-mix(in srgb,var(--game-colour-0) 35%,var(--game-white))'],
    ['100%', 'var(--game-colour-0)'],
  ]);
  if (position >= 0) {
    for (const neighbor of [route[position - 1], route[position + 1]]) {
      if (neighbor === undefined) continue;
      const dx = (neighbor % columns) - (index % columns),
        dy = Math.floor(neighbor / columns) - Math.floor(index / columns);
      if (Math.abs(dx) + Math.abs(dy) !== 1) continue;
      const edge = `${50 + dx * 50} ${50 + dy * 50}`;
      const d = neighbor === route[position - 1] ? `M${edge}L50 50` : `M50 50L${edge}`;
      for (const [width, stroke, opacity, cls] of [
        [38, 'var(--game-night)', 0.7, 'brook-bank'],
        [31, 'var(--game-colour-0)', 0.35, 'brook-shore'],
        [25, water, 1, 'brook-current'],
        [
          9,
          'color-mix(in srgb,var(--game-colour-0) 28%,var(--game-white))',
          0.85,
          'brook-water-core',
        ],
        [2.5, 'var(--game-white)', 0.95, 'brook-specular'],
      ])
        path(ctx, art, d, {
          class: cls,
          fill: 'none',
          stroke,
          opacity,
          'stroke-width': width,
          'stroke-linecap': 'round',
        });
    }
    circle(ctx, art, 50, 50, 12.5, { fill: water });
    circle(ctx, art, 50, 50, 4.5, {
      fill: 'color-mix(in srgb,var(--game-colour-0) 28%,var(--game-white))',
      opacity: 0.85,
    });
  }
  if (index === 0 || index === end) {
    circle(ctx, art, 50, 50, index === 0 ? 21 : 31, {
      class: 'brook-pool',
      fill: position >= 0 ? water : 'var(--game-night)',
      stroke: 'var(--game-colour-0)',
      'stroke-width': 3,
      opacity: 0.95,
    });
    for (const r of [9, 18])
      circle(ctx, art, 50, 50, r, {
        fill: 'none',
        stroke: 'var(--game-white)',
        'stroke-width': 1.2,
        opacity: position >= 0 ? 0.5 : 0.15,
      });
  } else if (cell.checkpoint) {
    const colour = `var(--game-colour-${[1, 5, 3, 2, 0][(cell.checkpoint - 1) % 5]})`;
    const petalPaint = paint(
      'petal',
      [
        ['0%', 'var(--game-white)'],
        ['25%', `color-mix(in srgb,${colour} 55%,var(--game-white))`],
        ['68%', colour],
        ['100%', `color-mix(in srgb,${colour} 72%,var(--game-night))`],
      ],
      true,
    );
    if (position >= 0) {
      circle(ctx, art, 50, 48, 33, {
        class: 'brook-bloom-halo',
        fill: 'none',
        stroke: 'var(--game-colour-3)',
        'stroke-width': 2,
        opacity: 0.55,
      });
      for (const x of [25, 75])
        path(
          ctx,
          art,
          `M${x} 68q${x < 50 ? -9 : 9} -13 ${x < 50 ? 10 : -10} -12q4 8 ${x < 50 ? -10 : 10} 12`,
          {
            fill: 'var(--game-colour-0)',
            stroke: 'var(--game-white)',
            'stroke-width': 1,
            opacity: 0.9,
          },
        );
    }
    for (let petal = 0; petal < 5; petal++) {
      const angle = (petal * Math.PI * 2) / 5;
      add(ctx, art, 'ellipse', {
        cx: 50 + Math.sin(angle) * 14,
        cy: 48 + Math.cos(angle) * 14,
        rx: 9,
        ry: 12,
        transform: `rotate(${-petal * 72} ${50 + Math.sin(angle) * 14} ${48 + Math.cos(angle) * 14})`,
        class: position >= 0 ? 'brook-petal brook-petal-bloomed' : 'brook-petal',
        fill: position >= 0 ? petalPaint : `color-mix(in srgb,${colour} 58%,var(--game-neutral))`,
        stroke: 'var(--game-white)',
        'stroke-width': 1,
        'stroke-opacity': position >= 0 ? 0.95 : 0.4,
      });
    }
    circle(ctx, art, 50, 48, 10, {
      fill:
        position >= 0
          ? 'color-mix(in srgb,var(--game-colour-3) 60%,var(--game-white))'
          : 'var(--game-colour-3)',
      stroke: 'var(--game-white)',
      'stroke-width': 1.5,
    });
    add(
      ctx,
      art,
      'text',
      {
        x: 50,
        y: 52,
        'text-anchor': 'middle',
        'font-size': 12,
        fill: 'var(--game-night)',
        'font-weight': 800,
      },
      String(cell.checkpoint),
    );
  } else if (position < 0 && cell.destination)
    circle(ctx, art, 50, 50, 6, {
      fill: 'var(--game-colour-0)',
      stroke: 'var(--game-white)',
      'stroke-width': 1,
      opacity: 0.65,
    });
  if (cell.bridge) {
    const rails = add(ctx, art, 'g', {
      class: 'brook-aqueduct',
      transform: cell.bridge === 'vertical' ? 'rotate(90 50 50)' : '',
    });
    for (const y of [23, 69]) {
      add(ctx, rails, 'rect', {
        x: 0,
        y: y + 4,
        width: 100,
        height: 12,
        rx: 3,
        fill: 'var(--game-shadow)',
      });
      add(ctx, rails, 'rect', {
        x: 0,
        y,
        width: 100,
        height: 9,
        rx: 3,
        fill: 'var(--brook-wood)',
        stroke: 'var(--brook-wood-light)',
        'stroke-width': 1,
      });
      path(ctx, rails, `M0 ${y + 3}Q24 ${y + 6} 48 ${y + 3}T100 ${y + 4}`, {
        fill: 'none',
        stroke: 'var(--brook-wood-dark)',
        'stroke-width': 1.5,
      });
      for (const x of [8, 48, 90])
        circle(ctx, rails, x, y + 4, 1.5, { fill: 'var(--game-white)', opacity: 0.65 });
    }
  }
  if (arrived)
    circle(ctx, art, 50, 50, 35, {
      class: 'brook-arrival',
      fill: 'none',
      stroke: 'var(--game-colour-3)',
      'stroke-width': 2,
      opacity: 0,
    });
  return art;
}
